import { axiosInterceptorInstance } from '../../axios-interceptor';
import { environment } from '../../environments/environment';

/**
 * REST client for the `buc-services-notifications` microservice.
 *
 * Base URL is `{environment.baseURL}/notifications`, which the portal proxy
 * rewrites to `/api/v1/notifications` on the service. See §3.2 of
 * `portal-notifications-definition.md` and the actual endpoint definitions in
 * `buc-services-notifications/src/Endpoints/NotificationEndpoints.cs`.
 *
 * Notes on the `state` query parameter:
 *   - The backend defaults to `state=active`; only open notifications are returned.
 *   - `state=return-to-normal` shows alarms that have returned to normal but have not been
 *     operator-closed.
 *   - Pass `includeClosed=true` to include items the operator has explicitly closed.
 *   - `DELETE /{siteId}/{notificationId}` is a *soft close*: it sets `Closed=true`
 *     and `ClosedAt` on the row. The row is still retrievable via `includeClosed=true`.
 */

const NOTIFICATION_BASE_URL = `${environment.baseURL}/notifications`;

// Keep degraded filtering explicit without changing the endpoint's data contract.
const withSilencingStatus = (response) => {
    const headers = response.headers;
    const status = headers?.get?.('X-Notification-Silencing')
        || headers?.['x-notification-silencing']
        || headers?.['X-Notification-Silencing'];
    return status === 'unavailable'
        ? { ...response.data, silencingUnavailable: true }
        : response.data;
};

export const fetchSilencedAlarmTypes = async () => {
    const response = await axiosInterceptorInstance.get(`${NOTIFICATION_BASE_URL}/silenced-alarm-types`);
    return response.data;
};

export const silenceAlarmType = async (alarmType) => {
    const response = await axiosInterceptorInstance.put(
        `${NOTIFICATION_BASE_URL}/silenced-alarm-types/${encodeURIComponent(alarmType)}`
    );
    if (response.status !== 204) throw new Error(`Unexpected status ${response.status}`);
    return response.status;
};

export const unsilenceAlarmType = async (alarmType) => {
    const response = await axiosInterceptorInstance.delete(
        `${NOTIFICATION_BASE_URL}/silenced-alarm-types/${encodeURIComponent(alarmType)}`
    );
    if (response.status !== 204) throw new Error(`Unexpected status ${response.status}`);
    return response.status;
};

/** Default page size used by both the page and the bell widget. */
export const DEFAULT_PAGE_SIZE = 25;

/**
 * Fetch a paginated list of notifications with optional filters.
 *
 * @param {Object} [params]
 * @param {string} [params.siteId]           Filter by site.
 * @param {string} [params.notificationType] `alarm` | `problem` | `report`.
 * @param {string} [params.severity]         `Info` | `Warning` | `Critical`.
 * @param {string} [params.bugReference]     Filter by bug reference, e.g. `YUK-36375`.
 * @param {string} [params.alarmId]          Filter by stable alarm id; returns every occurrence
 *                                            (Active → Normal → Active cycle) of the same alarm.
 * @param {string} [params.source]           Filter by producer/evidence source (e.g. `sli`,
 *                                            `crash-dump`). Accepts a single value or a
 *                                            comma-separated list — see
 *                                            `notification-source-concept.md` §5.
 * @param {string} [params.alarmType]         Comma-separated producer-supplied alarm type codes.
 * @param {string} [params.search]            Free-text search across title, description,
 *                                            bug reference, and site ID.
 * @param {string} [params.state]            `active` (default) | `return-to-normal` | `all`.
 * @param {boolean} [params.includeClosed]   Pass `true` to include operator-closed items.
 * @param {number} [params.pageSize]         Page size (1..100).
 * @param {string} [params.cursor]           Opaque signed previous/next cursor.
 * @param {string} [params.fromUtc]           Inclusive UTC lower bound for raisedAt.
 * @returns {Promise<{items: Array, totalCount: number, nextCursor: string|null, previousCursor: string|null, snapshotAt: string}>}
 */
export const fetchNotifications = async (params = {}) => {
    const query = new URLSearchParams();
    if (params.silenced) query.set('silenced', params.silenced);
    if (params.siteId) query.set('siteId', params.siteId);
    if (params.notificationType) query.set('notificationType', params.notificationType);
    if (params.severity) query.set('severity', params.severity);
    if (params.bugReference) query.set('bugReference', params.bugReference);
    if (params.alarmId) query.set('alarmId', params.alarmId);
    if (params.source) query.set('source', params.source);
    if (params.alarmType) query.set('alarmType', params.alarmType);
    if (params.search) query.set('search', params.search);
    if (params.state) query.set('state', params.state);
    if (params.includeClosed) query.set('includeClosed', 'true');
    if (params.fromUtc) query.set('fromUtc', params.fromUtc);
    if (params.toUtc) query.set('toUtc', params.toUtc);
    if (params.sort) query.set('sort', params.sort);
    query.set('pageSize', String(params.pageSize || DEFAULT_PAGE_SIZE));
    if (params.cursor) query.set('cursor', params.cursor);

    const qs = query.toString();
    const url = `${NOTIFICATION_BASE_URL}${qs ? `?${qs}` : ''}`;
    const response = await axiosInterceptorInstance.get(url);
    return withSilencingStatus(response);
};

/**
 * Fetch a single notification by composite key.
 *
 * @param {string} siteId
 * @param {string} notificationId
 * @returns {Promise<Object>}
 */
export const fetchNotification = async (siteId, notificationId) => {
    const url = `${NOTIFICATION_BASE_URL}/${encodeURIComponent(siteId)}/${encodeURIComponent(notificationId)}`;
    const response = await axiosInterceptorInstance.get(url);
    return response.data;
};

/**
 * Fetch aggregate counts of active (non-closed) notifications in a single
 * request. Used by the AppBar bell badge — avoids paginating through all
 * notifications just to count them.
 *
 * Maps to GET /api/v1/notifications/stats.
 * Response: { total, bySeverity: { critical, warning, info },
 *             byType: { alarm, problem, report },
 *             bySource: { [source]: number } }
 *
 * `bySource` is an open map — keys are whatever distinct `source` values are
 * currently active. The Notifications page uses these keys to populate the
 * multi-select Source filter.
 *
 * The bell badge displays bySeverity.critical + bySeverity.warning.
 *
 * @returns {Promise<{ total: number, bySeverity: { critical: number, warning: number, info: number }, byType: { alarm: number, problem: number, report: number }, bySource: Record<string, number> }>}
 */
export const fetchNotificationStats = async (params = {}) => {
    const query = new URLSearchParams();
    if (params.silenced) query.set('silenced', params.silenced);
    if (params.siteId) query.set('siteId', params.siteId);
    if (params.notificationType) query.set('notificationType', params.notificationType);
    const qs = query.toString();
    const response = await axiosInterceptorInstance.get(
        `${NOTIFICATION_BASE_URL}/stats${qs ? `?${qs}` : ''}`
    );
    return withSilencingStatus(response);
};

/**
 * Fetch distinct notification sites from `GET /api/v1/notifications/sites`.
 *
 * @param {Object} [params]
 * @param {string} [params.notificationType]
 * @param {string} [params.source]
 * @param {boolean} [params.includeClosed]
 * @param {string} [params.fromUtc]
 * @returns {Promise<{ sites: Array<{ siteId: string, siteName: string, notificationCount: number }>, totalSites: number }>}
 */
export const fetchNotificationSites = async (params = {}) => {
    const query = new URLSearchParams();
    if (params.silenced) query.set('silenced', params.silenced);
    if (params.notificationType) query.set('notificationType', params.notificationType);
    if (params.source) query.set('source', params.source);
    if (params.alarmType) query.set('alarmType', params.alarmType);
    if (params.includeClosed) query.set('includeClosed', 'true');
    if (params.fromUtc) query.set('fromUtc', params.fromUtc);
    const qs = query.toString();
    const response = await axiosInterceptorInstance.get(
        `${NOTIFICATION_BASE_URL}/sites${qs ? `?${qs}` : ''}`
    );
    return withSilencingStatus(response);
};

/**
 * Fetch distinct typed alarm counts without narrowing by the selected types.
 */
export const fetchNotificationAlarmTypes = async (filters = {}) => {
    const query = new URLSearchParams();
    if (filters.silenced) query.set('silenced', filters.silenced);
    ['siteId', 'source', 'severity', 'state', 'fromUtc', 'toUtc'].forEach((key) => {
        if (filters[key]) query.set(key, filters[key]);
    });
    if (filters.includeClosed) query.set('includeClosed', 'true');
    if (filters.includeTestNotifications) query.set('includeTestNotifications', 'true');
    const qs = query.toString();
    const response = await axiosInterceptorInstance.get(
        `${NOTIFICATION_BASE_URL}/alarm-types${qs ? `?${qs}` : ''}`
    );
    return withSilencingStatus(response);
};

/**
 * Close (soft-delete) a single notification. The row is retained in Table
 * Storage with `Closed=true` and `ClosedAt` set, so it can still be audited.
 * The `State` column is NOT changed — it continues to reflect the last
 * message-driven state (e.g. `active` or `return-to-normal`).
 *
 * @param {string} siteId
 * @param {string} notificationId
 * @returns {Promise<number>} HTTP status code (204 on success).
 */
export const clearNotification = async (siteId, notificationId) => {
    const url = `${NOTIFICATION_BASE_URL}/${encodeURIComponent(siteId)}/${encodeURIComponent(notificationId)}`;
    const response = await axiosInterceptorInstance.delete(url);
    return response.status;
};

/**
 * Close many notifications in a single round-trip via
 * `POST /api/v1/notifications/bulk-close`. Per-item behaviour is identical to
 * the single-item soft-close: rows are updated to `Closed=true`, `ClosedAt=now`;
 * `State` is left as-is. See §10.5 of `buc-notification-management-Definition.md`.
 *
 * The backend returns `200 OK` with a per-item result list — the request only
 * fails (HTTP 400) for malformed bodies. Infrastructure failures propagate as
 * thrown errors so the UI can surface a single retryable failure.
 *
 * @param {Array<{siteId: string, notificationId: string}>} targets
 * @returns {Promise<{ results: Array<{siteId: string, notificationId: string, status: 'closed'|'not-found'}>, closed: number, notFound: number }>}
 */
export const bulkCloseNotifications = async (targets) => {
    if (!Array.isArray(targets) || targets.length === 0) {
        return { results: [], closed: 0, notFound: 0 };
    }
    const url = `${NOTIFICATION_BASE_URL}/bulk-close`;
    const body = {
        items: targets.map((t) => ({
            siteId: t.siteId,
            notificationId: t.notificationId,
        })),
    };
    const response = await axiosInterceptorInstance.post(url, body);
    return response.data;
};

/**
 * Maps a raw API payload to the view model consumed by notification UI
 * components (page, detail, bell widget). Adds a stable composite `id` and a
 * numeric `raisedAtMs` to avoid repeated `Date` parsing in render paths.
 *
 * @param {Object} item
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const mapNotificationIdentity = (item) => ({
    id: `${item.siteId}/${item.notificationId}`,
    siteId: item.siteId,
    siteName: item.siteName ?? null,
    // Displayed in UI subtitles. Null when siteId is a UUID — GUIDs add no
    // value to operators; human-readable labels (e.g. 'yuk-qa27') are kept as-is.
    displaySiteId: UUID_RE.test(item.siteId) ? null : (item.siteId ?? null),
    notificationId: item.notificationId,
    // Stable alarm grouping id (e.g. "SLI-DB-001"). For alarms, `notificationId`
    // is the per-activation occurrence id; `alarmId` survives Active → Normal → Active
    // cycles so the UI can group multiple activations of the same condition.
    // Null for problems and reports.
    alarmId: item.alarmId ?? null,
});

const mapNotificationCore = (item) => ({
    alarmType: item.alarmType ?? null,
    title: item.title,
    description: item.description,
    severity: item.severity,
    notificationType: item.notificationType,
    // Producer-and-evidence identifier (e.g. 'sli', 'crash-dump', 'bug-pattern',
    // 'manual-operator'). Required by the API contract — see
    // `notification-source-concept.md` §2. List/detail components dispatch
    // on the `(notificationType, source)` pair to choose the right
    // drilldown / badge. Null only for legacy rows that pre-date the
    // schema-required `source` field.
    source: item.source ?? null,
    state: item.state,
    closed: item.closed ?? false,
    closedAt: item.closedAt ?? null,
    returnedToNormalAt: item.returnedToNormalAt ?? null,
    bugReference: item.bugReference,
    recommendation: item.recommendation,
    correlationId: item.correlationId,
    runId: item.runId,
});

const mapNotificationTimeline = (item) => ({
    // Optional data-point-aligned breach onset / recovery timestamps. When
    // present these align the notification's red-bar overlay with the
    // actual metric breach span instead of the producer's compute-cycle
    // wall-clock (`raisedAt` / `returnedToNormalAt`). Both are nullable
    // for backwards compatibility with producers that don't yet emit
    // them — callers must fall back to the wall-clock fields.
    sliTriggerAt: item.sliTriggerAt ?? null,
    sliRecoveryAt: item.sliRecoveryAt ?? null,
    // Scheduled DR event window. These fields drive a separate event
    // timeline and must not be interpreted as alarm recovery or closure.
    eventWindowStart: item.eventWindowStart ?? null,
    eventWindowEnd: item.eventWindowEnd ?? null,
    problemsCount: item.problemsCount ?? item.details?.problemsCount,
    devicesCount: item.devicesCount ?? item.details?.devicesCount,
    relatedEventCount: item.relatedEventCount ?? item.details?.relatedEventCount,
    relatedEventCountState: item.relatedEventCountState ?? item.details?.relatedEventCountState,
    // Optional cross-event correlation timeline. The notification service
    // flattens `data.details.*` onto the top-level DTO via JsonExtensionData,
    // so producers that publish `details.relatedEvents` surface here as
    // `relatedEvents` (legacy name `relatedEventsTimeline` is also supported).
    relatedEventsTimeline: item.relatedEventsTimeline ?? item.relatedEvents ?? item.details?.relatedEvents ?? null,
    measurementVerification: item.measurementVerification ?? null,
});

const mapNotificationEvidence = (item) => ({
    // Optional Yuki analysis-cost block. Populated only on `report` rows
    // whose `source === 'yuki'`. The portal's NotificationDetail page
    // uses presence of this object as the gating condition for the
    // analysis-cost panel — see yuki-cost-on-notification-implementation-plan.md §6.
    cost: item.cost ?? null,
    // Event-scoped DR connectivity artifact returned only by the existing
    // notification detail endpoint. No separate map request is required.
    connectivityMap: item.connectivityMap ?? null,
    // Optional DR-event confirmation latency distribution. Populated only
    // for report notifications whose source is 'dr-events-analysis' when
    // the producer has valid confirmed latency data. Returned only by the
    // detail endpoint — list and bell paths do not carry this field.
    confirmationTimeline: item.confirmationTimeline ?? item.details?.confirmationTimeline ?? null,
    // Optional diagnostic evidence for confirmed devices whose canonical
    // device-reported latency is negative. The complete ID list may be
    // omitted by transport while the total count and diagnostics remain.
    clockSkewDeviceCount: item.clockSkewDeviceCount ?? null,
    clockSkewDeviceIds: item.clockSkewDeviceIds ?? null,
    clockSkewDevices: item.clockSkewDevices ?? item.details?.clockSkewDevices ?? null,
    clockSkewDiagnostics: item.clockSkewDiagnostics ?? null,
    // Opaque report resource reference carried by alarm notifications.
    // The portal uses this only for navigation to report detail.
    reportReference: item.reportReference ?? item.details?.reportReference ?? null,
});

const mapNotificationTimestamps = (item) => {
    const raisedAt = item?.raisedAt ?? null;
    const raisedAtMs = raisedAt ? new Date(raisedAt).getTime() : null;
    return {
        raisedAt,
        raisedAtMs,
    };
};

export const mapNotification = (item) => ({
    ...mapNotificationIdentity(item),
    ...mapNotificationCore(item),
    ...mapNotificationTimeline(item),
    ...mapNotificationEvidence(item),
    ...mapNotificationTimestamps(item),
});

/**
 * Fetch every alarm notification for an SLI whose lifecycle overlaps a
 * UTC time window. Used by the standalone SLI drilldown
 * (`SliMetricDetail`) to overlay alarm markers on the metric chart.
 *
 * The notification list endpoint does not support time-window filtering
 * server-side, so this helper:
 *   1. Queries the list endpoint for `notificationType=alarm` on the site
 *      with `state=all` and `includeClosed=true` (so both currently-active
 *      alarms and already-returned-to-normal occurrences are returned).
 *   2. Filters client-side to alarms belonging to the requested SLI —
 *      either by stable `alarmId` prefix or, for legacy rows that lack an
 *      `alarmId`, by `notificationId` prefix.
 *   3. Keeps occurrences whose `[raisedAt, returnedToNormalAt|closedAt]`
 *      interval overlaps the requested window. Alarms that activated
 *      before the window and are still open are included.
 *
 * @param {string} siteId
 * @param {string} sliId
 * @param {{ startUtc?: string, endUtc?: string, take?: number }} [options]
 * @returns {Promise<Array>} Array of mapped notification view-models.
 */
export const fetchNotificationsForSli = async (siteId, sliId, options = {}) => {
    if (!siteId || !sliId) return [];

    const { startUtc, endUtc, take = 100 } = options;
    const startMs = startUtc ? new Date(startUtc).getTime() : Number.NEGATIVE_INFINITY;
    const endMs   = endUtc   ? new Date(endUtc).getTime()   : Number.POSITIVE_INFINITY;

    const { items = [] } = await fetchNotifications({
        siteId,
        notificationType: 'alarm',
        state: 'all',
        includeClosed: true,
        pageSize: take,
    });

    return items
        .map(mapNotification)
        .filter((n) => {
            // Match the SLI by stable alarm id when present (e.g. "SLI-DB-001-degraded"),
            // or fall back to the per-occurrence notificationId for legacy rows.
            const id = n.alarmId || n.notificationId || '';
            if (!id.startsWith(sliId)) return false;

            // Keep occurrences whose [trigger, recovery] interval overlaps
            // the window. Prefer the data-point-aligned `sliTriggerAt` /
            // `sliRecoveryAt` when the producer supplied them, falling back
            // to the compute-cycle `raisedAt` / `returnedToNormalAt|closedAt`.
            // An alarm that activated before the window and is still open (no
            // return-to-normal) is also included.
            const triggerIso = n.sliTriggerAt || null;
            const triggerMs = triggerIso ? new Date(triggerIso).getTime() : n.raisedAtMs;
            if (triggerMs == null) return false;
            const rtnIso = n.sliRecoveryAt || n.returnedToNormalAt || n.closedAt;
            const rtn = rtnIso ? new Date(rtnIso).getTime() : Number.POSITIVE_INFINITY;
            return triggerMs <= endMs && rtn >= startMs;
        });
};
